import { FC, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { observer } from "mobx-react-lite";
import "./Filter.css";
import { ListBox } from "primereact/listbox";
import { Calendar } from "primereact/calendar";
import Arrow from "../../../assets/arrowFilter.svg";
import Checkbox from "../Checkbox/Checkbox";
import { FilterOptionsExtented, SelectedOptions } from "../../../models/FilterModels/IFilterModel";
import { fullNameFormat } from "../../../helpers/Inicials";

interface FilterOpenableRowProps {
    label: string;
    selected: boolean;
    onClick: () => void;
}

const FilterOpenableRow: FC<FilterOpenableRowProps> = ({ label, selected, onClick }) => {
    return (
        <li className="filter_item " onClick={onClick}>
            <div className="filter_item_left">
                <img src={Arrow} />
                <div className="filter_label">{label}</div>
            </div>
            <div className="filter_check p_gray_light ">{selected ? "Выбрано" : "Не выбрано"}</div>
        </li>
    );
};

interface LabelIdFilterProps {
    label: string;
    open: boolean;
    options: { id: number | string; name: string }[];
    onOptionSelect: (id: number | string) => void;
    selectedOptions: (number | string)[] | undefined;
    setOpenFilter: (filter: string | undefined) => void;
}

const LabelIdFilter: FC<LabelIdFilterProps> = ({
    label,
    open,
    options,
    onOptionSelect,
    selectedOptions,
    setOpenFilter,
}) => {
    return (
        <div>
            <ul className="p_black p_12" style={{ position: "relative" }}>
                <FilterOpenableRow
                    label={label}
                    selected={selectedOptions !== undefined && selectedOptions.length > 0}
                    onClick={() => setOpenFilter(open ? undefined : label)}
                />
                {open ? (
                    <ul className="filter_items">
                        {options.map((option, index) => (
                            <li className="filter_item_inner">
                                <Checkbox
                                    id={index}
                                    handleSelection={() => onOptionSelect(option.id)}
                                    checked={selectedOptions?.some((oId) => oId === option.id)}
                                />
                                <div className="filter_label">{option.name}</div>
                            </li>
                        ))}
                    </ul>
                ) : null}
            </ul>
        </div>
    );
};

interface IFilterProps {
    options: FilterOptionsExtented | undefined;
    selectedOptions: SelectedOptions;
    setSelectedOptions: (options: SelectedOptions) => void;
    onApply?: () => Promise<void>;
    showApplyButton?: boolean;
}

const Filter: FC<IFilterProps> = ({ options, onApply, selectedOptions, setSelectedOptions, showApplyButton }) => {
    const [openFilter, setOpenFilter] = useState<string | undefined>();

    const toggleFilter = (key: string, id: number | string) => {
        //TODO Тут нужно подумать как привести к типу чтобы не сломалась логика
        const copy = JSON.parse(JSON.stringify(selectedOptions));

        if (!copy[key]) copy[key] = [];

        const exists = copy[key].includes(id);
        copy[key] = exists ? copy[key].filter((item: typeof id) => item !== id) : [...copy[key], id];

        setSelectedOptions(copy);
    };

    const selectDate = (dates: (Date | null)[]) => {
        const copy = {...selectedOptions} as SelectedOptions;
        if (dates[0] && dates[1]) {
            copy.dates = [dates[0], dates[1]];
        } else if (dates[0]) {
            copy.dates = [dates[0]];
        } else {
            copy.dates = undefined;
        }
        setSelectedOptions(copy);
    };

    const selectUsers = (userIds: (number | null)[] | undefined, usersCind: "responsible" | "historyAuthor") => {
        const copy = {...selectedOptions} as SelectedOptions;
        
        if (usersCind === "historyAuthor") {
            const filteredUserIds = userIds?.filter((id): id is number => {
                return id !== null;
            });
            copy.historyAuthorIds = filteredUserIds;
        }
        
        if (usersCind === "responsible") {
            copy.responsibleIds = userIds;
        }
        setSelectedOptions(copy);
    };

    const getNullableUserFilter = (
        label: string,
        open: boolean,
        options: { id: number; name: string }[],
        onOptionSelect: (ids: (number | null)[] | undefined) => void,
        selectedOptions: (number | null)[]
    ) => {
        let optionsWithNull = [...options, { id: -1, name: "Не указан" }];


        return (
            <div>
                <ul className="p_black p_12" style={{ position: "relative" }}>
                    <FilterOpenableRow
                        label={label}
                        selected={selectedOptions === null || selectedOptions?.length > 0}
                        onClick={() => setOpenFilter(open ? undefined : label)}
                    />
                    {open ? (
                        <div className="filter_items filter_users">
                            <ListBox
                                filterPlaceholder="Поиск..."
                                listStyle={{ maxHeight: "100px" }}
                                multiple
                                filter
                                value={optionsWithNull.filter((o) =>
                                    selectedOptions.some((so) => (so === null && o.id === -1) || so === o.id)
                                )}
                                onChange={(e) => {onOptionSelect(e.value.map((v: any) => (v.id !== -1 ? v.id : null)))}}
                                options={optionsWithNull}
                                optionLabel="name"
                                className="w-full md:w-14rem"
                            />
                        </div>
                    ) : null}
                </ul>
            </div>
        );
    };
    
    const getUserFilter = (
        label: string,
        open: boolean,
        options: { id: number; name: string }[],
        onOptionSelect: (ids: (number | null)[] | undefined) => void,
        selectedOptions: (number | null)[]
    ) => {
        return (
            <div>
                <ul className="p_black p_12" style={{ position: "relative" }}>
                    <FilterOpenableRow
                        label={label}
                        selected={selectedOptions === null || selectedOptions?.length > 0}
                        onClick={() => setOpenFilter(open ? undefined : label)}
                    />
                    {open ? (
                        <div className="filter_items filter_users">
                            <ListBox
                                filterPlaceholder="Поиск..."
                                listStyle={{ maxHeight: "100px" }}
                                multiple
                                filter
                                value={options.filter((o) =>
                                    selectedOptions.some((so) => so === o.id)
                                )}
                                onChange={(e) =>
                                    onOptionSelect(
                                        e.value.map((v: any) => v.id)
                                    )
                                }
                                options={options}
                                optionLabel="name"
                                className="w-full md:w-14rem"
                            />
                        </div>
                    ) : null}
                </ul>
            </div>
        );
    };

    const getDateFilter = (
        label: string,
        open: boolean,
        onOptionSelect: (dates: (Date | null)[]) => void,
        selectedDates: Date[]
    ) => {
        return (
            <div>
                <ul className="p_black p_12" style={{ position: "relative" }}>
                    <FilterOpenableRow
                        label={label}
                        selected={selectedDates.length > 0}
                        onClick={() => setOpenFilter(open ? undefined : label)}
                    />
                    {open ? (
                        <div className="filter_items">
                            <Calendar
                                value={selectedDates}
                                onChange={(e) => onOptionSelect(e.value ? e.value : [])}
                                selectionMode="range"
                                readOnlyInput
                                hideOnRangeSelection
                                dateFormat="dd.mm.yy"
                            />
                        </div>
                    ) : null}
                </ul>
            </div>
        );
    };

    return (
        <div className="filter_body">
            {options ? (
                <>
                    {options.date
                        ? getDateFilter("Дата", openFilter === "Дата", selectDate, selectedOptions.dates ?? [])
                        : null}
                    {options.responsible
                        ? getNullableUserFilter(
                              "Исполнитель",
                              openFilter === "Исполнитель",
                              options.responsible.map((r) => {
                                  return {
                                      id: r.id,
                                      name: fullNameFormat(r, "s N M"),
                                  };
                              }),
                              (ids: (number | null)[] | undefined) => {
                                  selectUsers(ids, "responsible");
                              },
                              selectedOptions.responsibleIds ?? []
                          )
                        : null}
                    {options.historyAuthor
                        ? getUserFilter(
                              "Пользователь",
                              openFilter === "Пользователь",
                              options.historyAuthor.map((r) => {
                                  return {
                                      id: r.id,
                                      name: fullNameFormat(r, "s N M"),
                                  };
                              }),
                              (ids: (number | null)[] | undefined) => {
                                  selectUsers(ids, "historyAuthor");
                              },
                              selectedOptions.historyAuthorIds ?? []
                          )
                        : null}
                    {options.priority ? (
                        <LabelIdFilter
                            label={"Приоритет"}
                            open={openFilter === "Приоритет"}
                            options={options.priority}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("priorityIds", id);
                            }}
                            selectedOptions={selectedOptions.priorityIds ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.typeTask ? (
                        <LabelIdFilter
                            label={"Тип задачи"}
                            open={openFilter === "Тип задачи"}
                            options={options.typeTask}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("typeTaskIds", id);
                            }}
                            selectedOptions={selectedOptions.typeTaskIds}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.status ? (
                        <LabelIdFilter
                            label={"Статус"}
                            open={openFilter === "Статус"}
                            options={options.status}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("statusIds", id);
                            }}
                            selectedOptions={selectedOptions.statusIds ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.role ? (
                        <LabelIdFilter
                            label={"Роль"}
                            open={openFilter === "Роль"}
                            options={options.role}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("roleIds", id);
                            }}
                            selectedOptions={selectedOptions.roleIds ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.entityType ? (
                        <LabelIdFilter
                            label={"Тип объекта"}
                            open={openFilter === "Тип объекта"}
                            options={options.entityType.map((type) => {
                                return { name: type.name, id: type.code };
                            })}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("entityTypes", id);
                            }}
                            selectedOptions={selectedOptions.entityTypes ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.actionType ? (
                        <LabelIdFilter
                            label={"Тип изменения"}
                            open={openFilter === "Тип изменения"}
                            options={options.actionType?.map((action) => {
                                return { id: action.code, name: action.name };
                            })}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("actionTypes", id);
                            }}
                            selectedOptions={selectedOptions.actionTypes ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                    {options.project ? (
                        <LabelIdFilter
                            label={"Проект"}
                            open={openFilter === "Проект"}
                            options={options.project}
                            onOptionSelect={(id: number | string) => {
                                toggleFilter("projectIds", id);
                            }}
                            selectedOptions={selectedOptions.projectIds ?? []}
                            setOpenFilter={setOpenFilter}
                        />
                    ) : null}
                </>
            ) : null}
            {showApplyButton ? (
                <div className="filterBtnApply">
                    <Button label="Применить" onClick={onApply} />
                </div>
            ) : null}
        </div>
    );
};

export default observer(Filter);
